import {
  IE3CL711,
  IE3ConsignmentProcess,
  IE3ConsignmentProcessSummary,
  IE3CustomsProcess,
  IE3FilingType,
  IE3Place,
  Port,
  Visit
} from "@portbase/bezoekschip-service-typescriptmodels";
import {ConsignmentUtils} from "./consignment.utils";
import {lodash, nonNull} from "../../common/utils";
import {AppContext} from "../../app-context";

export class ConsignmentRules {

  static telephoneRegex = /^\+(?:[0-9] ?){6,14}[0-9]$/;
  static containerRegex = /^[A-Za-z]{4}\d{7}$/;

  private static northernIrelandPorts: string[] = ["IEBEF", "GBBEL", "GBBAN", "GBLAR", "GBWPT"];

  static isCustomsOfficeFirstEntryAllowed(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isCustomsOfficeFirstEntryRequired(p)
  }

  static isCustomsOfficeFirstEntryRequired(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isEnsFiling(p);
  }

  static isSplintConsignmentAllowed(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isEnsFiling(p);
  }

  static isPlaceOfDeliveryMasterLevelAllowed(p: IE3ConsignmentProcess): boolean {
    return !ConsignmentRules.isStraightConsignment(p);
  }

  static isPlaceOfDeliveryMasterLevelRequired(p: IE3ConsignmentProcess): boolean {
    return !ConsignmentRules.isStraightConsignment(p) && ConsignmentRules.isMasterConsignment(p);
  }

  static isConsignorMasterLevelRequired(p: IE3ConsignmentProcess): boolean {
    return !ConsignmentRules.isStraightConsignment(p) && (ConsignmentRules.isMasterConsignment(p) || p.filing.filingType === 'TSD');
  }

  static isPlaceOfAcceptanceMasterLevelAllowed(p: IE3ConsignmentProcess): boolean {
    return !ConsignmentRules.isStraightConsignment(p)
  }

  static isPlaceOfAcceptanceMasterLevelRequired(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isMasterConsignment(p)
  }

  static isCustomsStatusAllowed(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isDutchPort(p);
  }

  static isCustomsProcessAllowed(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isDutchPort(p);
  }

  static isBulkAuthorizationAllowed(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isDutchPort(p);
  }

  static isDischargeTerminalAllowed(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isDutchPort(p);
  }

  static isExchangeRateAllowed(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isDutchPort(p);
  }

  static isCrnRequired(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isDutchPort(p)
  }

  static getPortEnteringEu(v: Visit, c: IE3ConsignmentProcess): Port {
    const placeOfLoading = c?.consignmentMasterLevel?.placeOfLoading;
    if (!v || !placeOfLoading) {
      return null;
    }
    const ports = v.visitDeclaration.previousPorts.map(p => p.port)
      .filter((p) => p.locationUnCode);
    if (ports.every(p => this.isEuCountryOrNorthernIreland(p.countryUnCode, p.locationUnCode))) {
      return null;
    }
    const portsAfterLoadingLocation = lodash.chain(ports)
      .takeWhile((p) => p.locationUnCode !== placeOfLoading.locationUnCode)
      .takeWhile((p) => this.isEuCountryOrNorthernIreland(p.countryUnCode, p.locationUnCode))
      .reverse().value();
    return portsAfterLoadingLocation?.length ? portsAfterLoadingLocation[0] : null;
  }

  static ensRequiredBasedOnItineraryOrPreviousDocument(v: Visit, c: IE3ConsignmentProcess): boolean {
    const previousDocument = c?.consignmentMasterLevel?.previousDocument;
    if (previousDocument && ConsignmentRules.isDutchPort(c) && ConsignmentUtils.previousDocumentWithEns(c.consignmentMasterLevel)) {
      return false;
    }
    const portItinerary = this.getPortItinerary(v);
    // When ens international is enabled an ENS is required when any country in the itinerary is outsideEurope.
    if (this.isEnsInternationalEnabledForDeclarant(c)) {
      if (!portItinerary) {
        return ConsignmentUtils.itineraryHasEUPort(c.consignmentMasterLevel.activeBorderTransportMeans.itinerary);
      }
      const placeOfLoadingUnCode = c?.consignmentMasterLevel?.placeOfLoading?.locationUnCode
      if (!placeOfLoadingUnCode) {
        return false;
      }
      const reversedPortItinerary = portItinerary.slice().reverse();
      const indexOfPlaceOfUnloadingInPortItinerary = reversedPortItinerary.findIndex((i) => i.port.locationUnCode == placeOfLoadingUnCode)
      if (indexOfPlaceOfUnloadingInPortItinerary == -1) {
        return false;
      }
      return reversedPortItinerary.slice(0, indexOfPlaceOfUnloadingInPortItinerary + 1)
        .some(p => !ConsignmentRules.isEuCountryOrNorthernIreland(p.port.countryUnCode, p.port.locationUnCode))
    }
    if (portItinerary?.length && portItinerary.length >= 1) {
      const previousPort = portItinerary[portItinerary.length - 1];
      if (previousPort) {
        return !ConsignmentRules.isEuCountryOrNorthernIreland(
          previousPort.port.countryUnCode, previousPort.port.locationUnCode);
      }
    }
    const placeOfUnloading = c?.consignmentMasterLevel?.placeOfUnloading;
    if (placeOfUnloading) {
      const itinerary = c.consignmentMasterLevel.activeBorderTransportMeans.itinerary;
      const indexOfPlaceOfUnloading = itinerary.indexOf(itinerary.find(i => i === placeOfUnloading.countryUnCode));
      if (indexOfPlaceOfUnloading < 0) {
        return true;
      }
      const countryBeforeUnloading = itinerary[indexOfPlaceOfUnloading === 0 ? indexOfPlaceOfUnloading : indexOfPlaceOfUnloading - 1];
      return !ConsignmentRules.isEuCountryOrNorthernIreland(countryBeforeUnloading, c?.consignmentMasterLevel?.placeOfLoading?.locationUnCode);
    }
    return true;
  }

  static isEnsInternationalEnabledForDeclarant(c: IE3ConsignmentProcess): boolean {
    const declarant = c?.filing?.declarant ? c.filing.declarant : AppContext.userProfile.organisation;
    return declarant.settings ? declarant.settings.declaresEnsInternational : false;
  }

  static getPortItinerary = (v: Visit) => {
    return v?.visitDeclaration?.previousPorts?.filter(p => p.port)?.map(p => (<PortItinerary>{
      port: p.port,
      departure: p.departure,
      arrival: p.arrival
    })).reverse();
  }

  static isVoyageNumberAllowed(p: IE3ConsignmentProcess): boolean {
    return !ConsignmentRules.isCrnRequired(p);
  }

  static isReEntryIndicatorAllowed(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isEnsFiling(p);
  }

  static isReEntryIndicatorRequired(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isEnsFiling(p);
  }

  static isTransportChargesAllowed(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isTransportChargesRequired(p);
  }

  static isTransportChargesRequired(p: IE3ConsignmentProcess): boolean {
    return ConsignmentRules.isMasterConsignment(p) || ConsignmentRules.isRoadConsignment(p);
  }

  static isTemporaryStorageFromEuCountryToNL = (p: IE3ConsignmentProcess) => {
    return ConsignmentRules.isDutchPort(p)
      && ConsignmentRules.isTemporaryStorageOnlyConsignment(p.filing.filingType)
      && p.consignmentMasterLevel.activeBorderTransportMeans.itinerary.every(c => ConsignmentRules.isEuCountryOrNorthernIreland(c, p.consignmentMasterLevel.placeOfLoading?.locationUnCode));
  }

  // static isDutchPort = (place: IE3Place) => place?.countryUnCode === "NL";

  static isTemporaryStorageOnlyConsignment = (filingType: IE3FilingType): boolean => [IE3FilingType.TSD]
    .includes(filingType);

  static isNoFilingConsignment = (filingType: IE3FilingType): boolean => [IE3FilingType.NONE]
    .includes(filingType);

  static isEnsFiling = (p: IE3ConsignmentProcess): boolean => [IE3FilingType.F10, IE3FilingType.F11, IE3FilingType.F12, IE3FilingType.F13, IE3FilingType.F50]
    .includes(p?.filing?.filingType);

  static isSummaryAllowedToDeclare = (consignment: IE3ConsignmentProcessSummary): boolean => !consignment.fromVisit;

  static isAllowedToDeclare = (consignment: IE3ConsignmentProcess): boolean => !consignment.fromVisit;

  static isMasterConsignment = (consignment: IE3ConsignmentProcess): boolean => [IE3FilingType.F11, IE3FilingType.F12]
    .includes(consignment.filing.filingType);

  static isStraightConsignment = (consignment: IE3ConsignmentProcess): boolean => consignment.filing.straight ||
    [IE3FilingType.F10, IE3FilingType.F13].includes(consignment.filing.filingType);

  static isStraight = (filingType: IE3FilingType, straight: boolean): boolean => straight ||
    [IE3FilingType.F10, IE3FilingType.F13].includes(filingType);

  static isRoadConsignment = (consignment: IE3ConsignmentProcess): boolean => [IE3FilingType.F50]
    .includes(consignment.filing.filingType);

  static isMultipleFilingConsignment = (consignment: IE3ConsignmentProcess): boolean => [IE3FilingType.F12, IE3FilingType.F13]
    .includes(consignment.filing.filingType);

  static isSingleFilingConsignment = (consignment: IE3ConsignmentProcess): boolean => [IE3FilingType.F10, IE3FilingType.F11, IE3FilingType.TSD]
    .includes(consignment.filing.filingType);

  static isSingleFilingConsignmentOrRoad = (consignment: IE3ConsignmentProcess): boolean => [IE3FilingType.F10, IE3FilingType.F11, IE3FilingType.F50]
    .includes(consignment.filing.filingType);

  static isPreviousDocumentAllowed(consignment: IE3ConsignmentProcess): boolean {
    return !ConsignmentRules.isNoFilingConsignment(consignment.filing.filingType);
  }

  static willWaitForEnsMrnForTsd(consignment: IE3ConsignmentProcess): boolean {
    if (ConsignmentRules.isNoFilingConsignment(consignment.filing.filingType)) {
      return false;
    }
    if (this.hasOnlyEmptyCarrierOwnedContainers(consignment)) {
      return false;
    }
    return ConsignmentRules.isDutchPort(consignment)
      && ConsignmentRules.isTemporaryStorageOnlyConsignment(consignment.filing.filingType)
      && ConsignmentRules.entersEuFromOutsideAfterLoading(consignment)
      && consignment.consignmentMasterLevel.customsProcess != IE3CustomsProcess.MILITARY;
  }

  /**
   * Whether, for this consignment's own journey, the goods enter the EU customs territory
   * from outside after being loaded - the condition that makes an ENS MRN mandatory.
   *
   * Scoped to this consignment's place of loading rather than the whole vessel rotation:
   * - a non-EU call that happened before the goods were loaded is irrelevant and ignored;
   * - goods loaded in the EU that leave and re-enter the EU before unloading still count;
   * - goods loaded outside the EU always count.
   *
   * The re-entry point is derived from the ordered itinerary (the first EU country that
   * follows a non-EU one at or after the loading position), so no non-EU country may appear
   * on the leg from the loading port up to and including the unloading port.
   */
  static entersEuFromOutsideAfterLoading(consignment: IE3ConsignmentProcess): boolean {
    const master = consignment.consignmentMasterLevel;
    const placeOfLoading = master.placeOfLoading;
    const itinerary = master.activeBorderTransportMeans?.itinerary ?? [];

    const loadIndex = itinerary.indexOf(placeOfLoading?.countryUnCode);
    const start = loadIndex >= 0 ? loadIndex : 0;
    const unloadIndex = itinerary.lastIndexOf(master.placeOfUnloading?.countryUnCode);
    const end = unloadIndex >= start ? unloadIndex + 1 : itinerary.length;

    // The loading port itself plus every country on the way to unloading.
    const countriesOnLeg = [placeOfLoading?.countryUnCode, ...itinerary.slice(start, end)];
    return countriesOnLeg.some(country =>
      country != null
      && !ConsignmentRules.isEuCountryOrNorthernIreland(country, placeOfLoading?.locationUnCode));
  }

  static hasOnlyEmptyCarrierOwnedContainers(consignment: IE3ConsignmentProcess): boolean {
    const equipments = Object.values(consignment.consignmentMasterLevel.transportEquipmentMap || {}).filter(nonNull);
    if (equipments.length === 0) {
      return false;
    }
    return equipments.every(e => e.empty === true) &&
      equipments.every(e => e.containerSupplierType === IE3CL711.N2);
  }

  // place?.countryUnCode === "NL";
  static isDutchPort(p: IE3ConsignmentProcess) {
    return p?.consignmentMasterLevel?.placeOfUnloading?.countryUnCode == 'NL';
  }

  static isEuPort = (place: IE3Place) => place?.euLocation;
  static isNorthernIrelandPort = (unloCode: string) => this.northernIrelandPorts.includes(unloCode);
  static isEuCountryOrNorthernIreland = (countryCode: string, unloCode: string) =>
    this.isNorthernIrelandPort(unloCode) || ConsignmentUtils.euCountries.includes(countryCode)

  static isShortLanded = (consignmentProcess: IE3ConsignmentProcess, equipmentNumber: string) =>
    consignmentProcess?.shortLandedContainers?.includes(equipmentNumber);
  static isDischarged = (consignmentProcess: IE3ConsignmentProcess, equipmentNumber: string) =>
    consignmentProcess?.dischargedContainers?.hasOwnProperty(equipmentNumber);

  static ensFilingRequired = (filingType: IE3FilingType) => [IE3FilingType.F10, IE3FilingType.F11, IE3FilingType.F12,
    IE3FilingType.F13, IE3FilingType.F50].includes(filingType);


}

export interface PortItinerary {
  port?: Port;
  arrival?: string;
  departure?: string;
}
